import { useState, useEffect } from "react";
import { Button } from "./Button";
import { TermsCheckbox } from "./form/TermsCheckbox";
import { StandardField } from "../Forms/templates/components/StandardField";
import { validate } from "../../utils/form-functions";
import { formComponent } from "../../datasets/formComponent";
import { sendGaEvent, sendLinkedInConversion } from "../../../config/userTracking";
import { userApi } from "../../../api/userApi";
import { publicApi } from "../../../api/publicApi";
import { formAttribution } from "../../../utils/formAttribution";
import { useRouter } from "next/router";

export const StandardFormComponent = ({
  data,
  changeModalOpen,
  changeErrorModalOpen,
  pipedriveType,
  formName,
  gaName, // if not provided, formName will be taken
  caseStudy,
  resetValuesAfterwards = false, // sometimes we want to remove values after submit, usually not
  isConsent = true, // some forms do not have consent as an option, but most do
  customButtonText, // defaults to "Submit"
  isButtonInverted = false, // inverts colors of sumbit button
  setFlagPresent, // video form sets flag informing it was used
  webinarIdZoom, // only on webinar registration - we make additional request on that page
  setModalOpen, // only on report2022 - successful action results in opening custom modal
}) => {
  const [formData, changeFormData] = useState({});
  const [errors, changeErrors] = useState({});
  const [consent, changeConsent] = useState(false);
  const [sending, changeSending] = useState(false);
  const router = useRouter();
  const pathname = router.asPath;

  const [linkedInCookie, setLinkedInCookie] = useState(null);

  useEffect(() => {
    setLinkedInCookie(localStorage.getItem("li_fat_id"));
  }, []);

  const isFirstLastNamePresent = fields => {
    const isFirstName = typeof fields.first_name !== "undefined";
    const isLastName = typeof fields.last_name !== "undefined";

    if (!isFirstName && !isLastName) return false;
    return true;
  };

  const resetForm = () => {
    let newFormData = {};
    data.fields.forEach(field => {
      if (field.component !== "standard-multielement") {
        newFormData[field.id] = "";
      } else {
        field.fields.forEach(subfield => {
          newFormData[subfield.id] = "";
        });
      }
    });
    if (isFirstLastNamePresent(newFormData) === true)
      newFormData.full_name = `${newFormData.first_name} ${newFormData.last_name}`;
    changeFormData(newFormData);
    changeConsent(false);
  };

  const changeData = (e, data) => {
    let newFormData = { ...formData };
    newFormData[data.id] = e;
    if (data.id === "first_name" || data.id === "last_name") {
      newFormData.full_name = `${newFormData.first_name} ${newFormData.last_name}`;
    }
    changeFormData(newFormData);
  };

  const preparedData = () => {
    let preparedData = formAttribution(pipedriveType);
    if (formName !== "Catalog Bottom") {
      preparedData = { ...preparedData, ...formData };
    } else {
      preparedData.full_name = formData.full_name_bottom;
      preparedData.email = formData.email_bottom;
    }
    return preparedData;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (
      !sending &&
      validate({ formData, data, changeErrors, consent: isConsent ? consent : true }) === true
    ) {
      changeSending(true);
      const dataToSend = {
        type: formName,
        formValues: preparedData(),
      };

      linkedInCookie && sendLinkedInConversion();
      const setUserTracking = () => {
        let formattedUserToTrack = {
          user: {
            email: formData.email,
            name: formData.full_name,
          },
        };
        if (formData.company_name || formData.company_name === "")
          formattedUserToTrack.organization = { name: formData.company_name || "" };
        if (formName === "Catalog Bottom") {
          formattedUserToTrack.user = {
            email: formData.email_bottom,
            name: formData.full_name_bottom,
          };
        }
        const gaEmail = formName === "Catalog Bottom" ? formData.email_bottom : formData.email;
        let urlPath = pathname;
        if (formName === "Solutions whitepaper") urlPath = "/solution/whitepaper";
        if (formName === "Webinar video") urlPath = "/webinar_video";

        sendGaEvent(
          {
            action: "Form Submission",
            category: "Lead",
            label: `Submit - ${gaName ? gaName : formName} Form - ${urlPath}`,
          },
          gaEmail,
        );
      };

      try {
        if (formName === "Webinar registration") {
          const registrantPayload = {
            email: formData.email,
            full_name: formData.full_name,
            company_name: formData.company_name || "",
            job_title: formData.job_title || "",
            message: formData.message || "",
          };
          const webinarId = webinarIdZoom.replaceAll(" ", "");
          await publicApi.sendRegistrant(webinarId, registrantPayload);
        }
        dataToSend.formValues.case_study = caseStudy;
        await userApi.sendFrontForm(dataToSend);
        if (formName === "Webinar video") {
          setFlagPresent && setFlagPresent(true);
          localStorage.setItem("video_form_filled", true);
        }
        setUserTracking();
        resetValuesAfterwards && resetForm();
        changeModalOpen && changeModalOpen(true);
        setModalOpen && setModalOpen(true);
        changeSending(false);
      } catch (e) {
        changeErrorModalOpen && changeErrorModalOpen(true);
        changeSending(false);
      }
    }
  };

  useEffect(() => {
    resetForm();
  }, []);

  return (
    Object.keys(formData).length !== 0 && (
      <>
        <form onSubmit={handleSubmit} noValidate title={formName}>
          <StandardField
            fields={data.fields}
            formData={formData}
            changeData={changeData}
            errors={errors}
          />
          {isConsent && (
            <>
              <section id="consent" />
              <TermsCheckbox
                text={formComponent.form.consent}
                checked={consent}
                onChange={changeConsent}
                error={errors.consent}
              />
            </>
          )}
          <section>
            <Button
              disabled={sending}
              variant={isButtonInverted ? "inverted" : "primary"}
              demystIcon
            >
              {customButtonText || formComponent.button_text}
            </Button>
          </section>
        </form>
      </>
    )
  );
};
